import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '@/stores/auth.ts'
import { apiClient } from '@/lib/api.ts'
import {
  Plus,
  ThumbsUp,
  MessageSquare,
  Send,
  Lightbulb,
  ArrowLeft,
  Trash2,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  LoadingState,
  PageHeader,
  PriorityBadge,
  Select,
  StatusBadge,
  Textarea,
  useToast,
} from '@/components/ui'
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  type Priority,
} from '@/styles/tokens.ts'
import { cn } from '@/lib/utils.ts'

// ─── Types ───────────────────────────────────────────────────────────────────

type FeatureStatus = 'submitted' | 'reviewing' | 'planned' | 'implemented' | 'rejected'
type FeaturePriority = Extract<Priority, 'nice_to_have' | 'important' | 'critical'>

interface FeatureRequest {
  id: string
  title: string
  description: string
  category: string | null
  reasoning: string | null
  affectedUsers: string | null
  priority: FeaturePriority
  businessImpact: string | null
  status: FeatureStatus
  adminComment: string | null
  targetRelease: string | null
  relatedRequestId: string | null
  submittedByUserId: string
  submittedByName: string | null
  createdAt: string
  updatedAt: string
  voteCount: number
  hasVoted: boolean
  commentCount: number
}

interface Comment {
  id: string
  content: string
  userId: string
  userName: string | null
  createdAt: string
}

interface FeatureDetail extends FeatureRequest {
  comments: Comment[]
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FEATURE_STATUSES: FeatureStatus[] = [
  'submitted',
  'reviewing',
  'planned',
  'implemented',
  'rejected',
]

const CATEGORIES = [
  'Dashboard',
  'Tickets',
  'Zeiterfassung',
  'Netzwerk',
  'Monitoring',
  'Passwörter',
  'SAP / Aufträge',
  'Marketplace',
  'Versand',
  'Einstellungen',
  'Allgemein',
  'Sonstiges',
]

// ─── Main Page ───────────────────────────────────────────────────────────────

export function FeaturesPage() {
  const { toast } = useToast()
  const [features, setFeatures] = useState<FeatureRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const fetchFeatures = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/features')
      setFeatures(data)
    } catch {
      // read-only: stilles Fallback auf leere Liste (siehe CLAUDE.md)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFeatures()
  }, [fetchFeatures])

  const handleVote = async (id: string) => {
    try {
      const { data } = await apiClient.post(`/features/${id}/vote`)
      setFeatures((prev) =>
        prev.map((f) =>
          f.id === id
            ? { ...f, hasVoted: data.voted, voteCount: f.voteCount + (data.voted ? 1 : -1) }
            : f,
        ),
      )
    } catch {
      toast({ title: 'Stimme konnte nicht gespeichert werden', tone: 'danger' })
    }
  }

  const handleCreated = () => {
    setShowCreate(false)
    fetchFeatures()
    toast({ title: 'Feature-Wunsch eingereicht', tone: 'success' })
  }

  const confirmDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await apiClient.delete(`/features/${deleteId}`)
      setFeatures((prev) => prev.filter((f) => f.id !== deleteId))
      if (selectedId === deleteId) setSelectedId(null)
      toast({ title: 'Feature-Wunsch gelöscht', tone: 'success' })
      setDeleteId(null)
    } catch {
      toast({ title: 'Löschen fehlgeschlagen', tone: 'danger' })
    } finally {
      setDeleting(false)
    }
  }

  const filteredFeatures =
    filterStatus === 'all' ? features : features.filter((f) => f.status === filterStatus)

  if (selectedId) {
    return (
      <FeatureDetailView
        featureId={selectedId}
        onBack={() => {
          setSelectedId(null)
          fetchFeatures()
        }}
        onDeleteRequest={(id) => setDeleteId(id)}
      />
    )
  }

  if (showCreate) {
    return <CreateFeatureForm onCancel={() => setShowCreate(false)} onCreated={handleCreated} />
  }

  const statusCounts = features.reduce<Record<string, number>>((acc, f) => {
    acc[f.status] = (acc[f.status] ?? 0) + 1
    return acc
  }, {})

  return (
    <div className="p-6 max-w-4xl">
      <PageHeader
        title="Feature-Wünsche"
        description="Ideen einreichen, abstimmen und den Status verfolgen"
        actions={
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Plus className="h-4 w-4" />}
            onClick={() => setShowCreate(true)}
          >
            Neuer Wunsch
          </Button>
        }
      />

      <FilterBar className="mb-4">
        <FilterChip
          active={filterStatus === 'all'}
          count={features.length}
          onClick={() => setFilterStatus('all')}
        >
          Alle
        </FilterChip>
        {FEATURE_STATUSES.map((status) => (
          <FilterChip
            key={status}
            active={filterStatus === status}
            count={statusCounts[status] ?? 0}
            onClick={() => setFilterStatus(status)}
          >
            {STATUS_LABELS[status]}
          </FilterChip>
        ))}
      </FilterBar>

      {loading ? (
        <LoadingState label="Feature-Wünsche werden geladen…" />
      ) : filteredFeatures.length === 0 ? (
        <Card>
          <Card.Body>
            <EmptyState
              icon={<Lightbulb />}
              title={
                filterStatus === 'all'
                  ? 'Noch keine Feature-Wünsche'
                  : 'Keine Einträge mit diesem Status'
              }
              description={
                filterStatus === 'all'
                  ? 'Reiche den ersten Wunsch ein und lasse andere abstimmen.'
                  : undefined
              }
              action={
                filterStatus === 'all' ? (
                  <Button
                    variant="primary"
                    size="sm"
                    leadingIcon={<Plus className="h-4 w-4" />}
                    onClick={() => setShowCreate(true)}
                  >
                    Neuer Wunsch
                  </Button>
                ) : undefined
              }
            />
          </Card.Body>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {filteredFeatures.map((feature) => (
            <FeatureRow
              key={feature.id}
              feature={feature}
              onVote={handleVote}
              onClick={() => setSelectedId(feature.id)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteId !== null}
        title="Feature-Wunsch löschen?"
        message="Diese Aktion kann nicht rückgängig gemacht werden."
        confirmLabel="Löschen"
        variant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  )
}

// ─── Feature Row ─────────────────────────────────────────────────────────────

function FeatureRow({
  feature,
  onVote,
  onClick,
}: {
  feature: FeatureRequest
  onVote: (id: string) => void
  onClick: () => void
}) {
  return (
    <Card className="hover:border-gray-300 transition-colors">
      <Card.Body className="flex items-center gap-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onVote(feature.id)
          }}
          className={cn(
            'flex flex-col items-center justify-center w-12 h-12 rounded-md border text-caption font-medium transition-colors shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
            feature.hasVoted
              ? 'border-brand-500 bg-brand-50 text-brand-700'
              : 'border-gray-200 bg-white text-gray-500 hover:border-brand-300 hover:text-brand-600',
          )}
          aria-pressed={feature.hasVoted}
          aria-label={feature.hasVoted ? 'Stimme entfernen' : 'Abstimmen'}
        >
          <ThumbsUp className={cn('h-3.5 w-3.5 mb-0.5', feature.hasVoted && 'fill-brand-500')} />
          {feature.voteCount}
        </button>

        <button
          type="button"
          onClick={onClick}
          className="flex-1 min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded"
        >
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="text-body font-medium text-gray-900 truncate">{feature.title}</p>
            <StatusBadge status={feature.status} size="sm" />
            <PriorityBadge priority={feature.priority} size="sm" />
          </div>
          <div className="flex items-center gap-3 text-caption text-gray-500">
            {feature.submittedByName ? <span>{feature.submittedByName}</span> : null}
            <span>{new Date(feature.createdAt).toLocaleDateString('de-DE')}</span>
            {feature.category ? <span>{feature.category}</span> : null}
            {feature.commentCount > 0 ? (
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {feature.commentCount}
              </span>
            ) : null}
          </div>
        </button>
      </Card.Body>
    </Card>
  )
}

// ─── Create Form ─────────────────────────────────────────────────────────────

function CreateFeatureForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: () => void
}) {
  const { toast } = useToast()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    reasoning: '',
    affectedUsers: '',
    priority: 'nice_to_have' as FeaturePriority,
    businessImpact: '',
  })

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      await apiClient.post('/features', form)
      onCreated()
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: { error?: string; details?: Record<string, string[]> } } })?.response?.data
      if (resp?.details) {
        const messages = Object.entries(resp.details)
          .map(([field, errs]) => `${field}: ${(errs as string[]).join(', ')}`)
          .join('\n')
        setError(messages)
      } else {
        const msg = resp?.error || 'Fehler beim Speichern.'
        setError(msg)
        toast({ title: 'Speichern fehlgeschlagen', description: msg, tone: 'danger' })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-6 max-w-3xl">
      <Button
        variant="ghost"
        size="sm"
        leadingIcon={<ArrowLeft className="h-4 w-4" />}
        onClick={onCancel}
        className="mb-2"
      >
        Zurück
      </Button>

      <PageHeader title="Neuen Feature-Wunsch einreichen" />

      <Card>
        <Card.Body>
          <form onSubmit={submit} className="flex flex-col gap-5">
            {error ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-small text-red-700 whitespace-pre-wrap">
                {error}
              </div>
            ) : null}

            <Input
              label="Titel"
              required
              placeholder="Kurze, prägnante Beschreibung"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
            />

            <Textarea
              label="Beschreibung"
              required
              rows={4}
              placeholder="Ausführliche Beschreibung des gewünschten Features…"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Kategorie / Modul"
                placeholder="— Bitte wählen —"
                value={form.category}
                onValueChange={(v) => set('category', v)}
                options={CATEGORIES.map((c) => ({ value: c, label: c }))}
              />

              <Select<FeaturePriority>
                label="Priorität"
                value={form.priority}
                onValueChange={(v) => set('priority', v)}
                options={[
                  { value: 'nice_to_have', label: PRIORITY_LABELS.nice_to_have },
                  { value: 'important', label: PRIORITY_LABELS.important },
                  { value: 'critical', label: PRIORITY_LABELS.critical },
                ]}
              />
            </div>

            <Textarea
              label="Begründung"
              rows={3}
              placeholder="Welches Problem wird gelöst oder welcher Nutzen entsteht?"
              value={form.reasoning}
              onChange={(e) => set('reasoning', e.target.value)}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Betroffener Nutzerkreis"
                placeholder="z.B. Alle Techniker (ca. 15 Personen)"
                value={form.affectedUsers}
                onChange={(e) => set('affectedUsers', e.target.value)}
              />

              <Input
                label="Business Impact"
                placeholder="z.B. Zeitersparnis von ca. 2h/Woche"
                value={form.businessImpact}
                onChange={(e) => set('businessImpact', e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button
                type="submit"
                variant="primary"
                loading={saving}
                leadingIcon={<Send className="h-4 w-4" />}
              >
                Einreichen
              </Button>
              <Button type="button" variant="ghost" onClick={onCancel}>
                Abbrechen
              </Button>
            </div>
          </form>
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Detail View ─────────────────────────────────────────────────────────────

function FeatureDetailView({
  featureId,
  onBack,
  onDeleteRequest,
}: {
  featureId: string
  onBack: () => void
  onDeleteRequest: (id: string) => void
}) {
  const { toast } = useToast()
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin'

  const [feature, setFeature] = useState<FeatureDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [comment, setComment] = useState('')
  const [sendingComment, setSendingComment] = useState(false)
  const [savingStatus, setSavingStatus] = useState(false)
  const [statusUpdate, setStatusUpdate] = useState<{
    status: FeatureStatus
    adminComment: string
    targetRelease: string
  }>({ status: 'submitted', adminComment: '', targetRelease: '' })

  useEffect(() => {
    apiClient
      .get(`/features/${featureId}`)
      .then(({ data }) => {
        setFeature(data)
        setStatusUpdate({
          status: data.status,
          adminComment: data.adminComment || '',
          targetRelease: data.targetRelease || '',
        })
      })
      .catch(() => onBack())
      .finally(() => setLoading(false))
  }, [featureId, onBack])

  const sendComment = async () => {
    if (!comment.trim()) return
    setSendingComment(true)
    try {
      await apiClient.post(`/features/${featureId}/comments`, { content: comment })
      setComment('')
      const { data } = await apiClient.get(`/features/${featureId}`)
      setFeature(data)
    } catch {
      toast({ title: 'Kommentar konnte nicht gespeichert werden', tone: 'danger' })
    } finally {
      setSendingComment(false)
    }
  }

  const updateStatus = async () => {
    setSavingStatus(true)
    try {
      await apiClient.patch(`/features/${featureId}/status`, statusUpdate)
      const { data } = await apiClient.get(`/features/${featureId}`)
      setFeature(data)
      toast({ title: 'Status aktualisiert', tone: 'success' })
    } catch {
      toast({ title: 'Status konnte nicht aktualisiert werden', tone: 'danger' })
    } finally {
      setSavingStatus(false)
    }
  }

  if (loading || !feature) {
    return (
      <div className="p-6">
        <LoadingState label="Feature-Wunsch wird geladen…" />
      </div>
    )
  }

  const isOwner = feature.submittedByUserId === currentUser?.id

  return (
    <div className="p-6 max-w-3xl">
      <Button
        variant="ghost"
        size="sm"
        leadingIcon={<ArrowLeft className="h-4 w-4" />}
        onClick={onBack}
        className="mb-2"
      >
        Zurück zur Liste
      </Button>

      <PageHeader
        title={feature.title}
        actions={
          isOwner || isAdmin ? (
            <IconButton
              variant="danger"
              size="sm"
              icon={<Trash2 />}
              aria-label="Feature-Wunsch löschen"
              onClick={() => onDeleteRequest(feature.id)}
            />
          ) : undefined
        }
      />

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <StatusBadge status={feature.status} />
        <PriorityBadge priority={feature.priority} />
        {feature.category ? <Badge variant="neutral">{feature.category}</Badge> : null}
        <span className="text-caption text-gray-500">
          {feature.submittedByName ? `von ${feature.submittedByName} ` : ''}am{' '}
          {new Date(feature.createdAt).toLocaleDateString('de-DE')}
        </span>
      </div>

      <Card className="mb-6">
        <Card.Body className="flex flex-col gap-4">
          <DetailSection title="Beschreibung">{feature.description}</DetailSection>
          {feature.reasoning ? (
            <DetailSection title="Begründung">{feature.reasoning}</DetailSection>
          ) : null}
          {feature.affectedUsers ? (
            <DetailSection title="Betroffener Nutzerkreis">{feature.affectedUsers}</DetailSection>
          ) : null}
          {feature.businessImpact ? (
            <DetailSection title="Business Impact">{feature.businessImpact}</DetailSection>
          ) : null}
          {feature.adminComment ? (
            <DetailSection title="Feedback vom Produktteam">
              <span className="text-brand-700">{feature.adminComment}</span>
            </DetailSection>
          ) : null}
          {feature.targetRelease ? (
            <DetailSection title="Ziel-Release">{feature.targetRelease}</DetailSection>
          ) : null}
        </Card.Body>
      </Card>

      {isAdmin ? (
        <Card className="mb-6">
          <Card.Header>
            <h3 className="text-h4 text-gray-900">Admin-Bearbeitung</h3>
          </Card.Header>
          <Card.Body className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select<FeatureStatus>
                label="Status"
                value={statusUpdate.status}
                onValueChange={(v) => setStatusUpdate((s) => ({ ...s, status: v }))}
                options={FEATURE_STATUSES.map((key) => ({
                  value: key,
                  label: STATUS_LABELS[key] ?? key,
                }))}
              />
              <div className="sm:col-span-2">
                <Input
                  label="Ziel-Release"
                  placeholder="z.B. v2.5 / Q2 2026"
                  value={statusUpdate.targetRelease}
                  onChange={(e) =>
                    setStatusUpdate((s) => ({ ...s, targetRelease: e.target.value }))
                  }
                />
              </div>
            </div>
            <Textarea
              label="Kommentar / Feedback"
              rows={2}
              placeholder="Feedback oder Begründung für die Entscheidung…"
              value={statusUpdate.adminComment}
              onChange={(e) =>
                setStatusUpdate((s) => ({ ...s, adminComment: e.target.value }))
              }
            />
            <div>
              <Button variant="primary" size="sm" onClick={updateStatus} loading={savingStatus}>
                Status aktualisieren
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      <div className="flex items-center gap-2 mb-6 text-small text-gray-600">
        <ThumbsUp className="h-4 w-4" />
        {feature.voteCount} {feature.voteCount === 1 ? 'Stimme' : 'Stimmen'}
      </div>

      <Card>
        <Card.Header>
          <h3 className="text-h4 text-gray-900">
            Kommentare ({feature.comments.length})
          </h3>
        </Card.Header>
        <Card.Body className="flex flex-col gap-3">
          {feature.comments.length > 0 ? (
            <div className="flex flex-col gap-3">
              {feature.comments.map((c) => (
                <div
                  key={c.id}
                  className="border border-gray-200 rounded-md p-3 bg-white"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-caption font-medium text-gray-900">
                      {c.userName}
                    </span>
                    <span className="text-caption text-gray-500">
                      {new Date(c.createdAt).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <p className="text-small text-gray-700 whitespace-pre-wrap">
                    {c.content}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-small text-gray-500">Noch keine Kommentare.</p>
          )}

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                placeholder="Kommentar schreiben…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    sendComment()
                  }
                }}
              />
            </div>
            <Button
              variant="primary"
              onClick={sendComment}
              loading={sendingComment}
              disabled={!comment.trim()}
              leadingIcon={<Send className="h-4 w-4" />}
            >
              Senden
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function DetailSection({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div>
      <p className="text-caption font-medium text-gray-500 uppercase tracking-wider mb-1">
        {title}
      </p>
      <p className="text-small text-gray-700 whitespace-pre-wrap">{children}</p>
    </div>
  )
}
